import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LanguageIcon from '@mui/icons-material/Language';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const SectionContainer = styled(Box)({
  padding: '140px 60px',
  background: '#ffffff',
  position: 'relative',
  overflow: 'hidden',
  '@media (max-width: 1024px)': {
    padding: '100px 32px'
  },
  '@media (max-width: 768px)': {
    padding: '80px 20px'
  }
});

const ContentWrapper = styled(Box)({
  maxWidth: '1400px',
  margin: '0 auto'
});

const MainContent = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '1.2fr 1fr',
  gap: '100px',
  alignItems: 'center',
  '@media (max-width: 1024px)': {
    gap: '60px'
  },
  '@media (max-width: 900px)': {
    gridTemplateColumns: '1fr',
    gap: '48px'
  }
});

const LeftColumn = styled(Box)({
  animation: `${fadeInUp} 0.8s ease-out`
});

const SectionBadge = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 24px',
  background: 'rgba(201, 162, 39, 0.1)',
  border: '1px solid rgba(201, 162, 39, 0.2)',
  borderRadius: '50px',
  marginBottom: '28px'
});

const BadgeText = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '12px',
  fontWeight: 600,
  color: '#c9a227',
  textTransform: 'uppercase',
  letterSpacing: '2px'
});

const SectionTitle = styled(Typography)({
  fontFamily: "'Playfair Display', serif",
  fontSize: 'clamp(36px, 5vw, 56px)',
  fontWeight: 400,
  color: '#1a1a1a',
  lineHeight: 1.2,
  letterSpacing: '-1px',
  marginBottom: '32px'
});

const GradientText = styled('span')({
  background: 'linear-gradient(135deg, #c9a227 0%, #a67c52 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text'
});

const DescriptionText = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '17px',
  fontWeight: 400,
  color: '#666666',
  lineHeight: 1.9,
  marginBottom: '40px'
});

const StatsRow = styled(Box)({
  display: 'flex',
  gap: '48px',
  marginBottom: '48px',
  '@media (max-width: 600px)': {
    flexWrap: 'wrap',
    gap: '32px'
  }
});

const StatItem = styled(Box)({
  textAlign: 'left'
});

const StatNumber = styled(Typography)({
  fontFamily: "'Playfair Display', serif",
  fontSize: '48px',
  fontWeight: 400,
  color: '#c9a227',
  lineHeight: 1,
  marginBottom: '8px'
});

const StatLabel = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '14px',
  fontWeight: 500,
  color: '#888888'
});

const QuoteBlock = styled(Box)({
  position: 'relative',
  padding: '32px 0 32px 32px',
  borderLeft: '3px solid',
  borderImage: 'linear-gradient(180deg, #c9a227, #e8d5a3) 1'
});

const QuoteText = styled(Typography)({
  fontFamily: "'Playfair Display', serif",
  fontSize: '22px',
  fontWeight: 400,
  fontStyle: 'italic',
  color: '#333333',
  lineHeight: 1.7
});

const RightColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  animation: `${fadeInUp} 0.8s ease-out 0.2s both`
});

const InfoCard = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '20px',
  padding: '28px 32px',
  background: '#faf9f7',
  borderRadius: '20px',
  cursor: 'pointer',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  border: '1px solid transparent',
  '&:hover': {
    background: '#ffffff',
    borderColor: 'rgba(201, 162, 39, 0.3)',
    boxShadow: '0 15px 50px rgba(0, 0, 0, 0.08)',
    transform: 'translateX(8px)',
    '& .card-icon': {
      background: 'linear-gradient(135deg, #c9a227 0%, #e8d5a3 100%)',
      '& svg': {
        color: '#ffffff'
      }
    },
    '& .card-arrow': {
      opacity: 1,
      transform: 'translateX(0)'
    }
  }
});

const IconBox = styled(Box)({
  width: '56px',
  height: '56px',
  borderRadius: '16px',
  background: 'rgba(201, 162, 39, 0.1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.3s ease',
  '& svg': {
    fontSize: '24px',
    color: '#c9a227',
    transition: 'color 0.3s ease'
  }
});

const CardContent = styled(Box)({
  flex: 1
});

const CardLabel = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '12px',
  fontWeight: 600,
  color: '#c9a227',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  marginBottom: '6px'
});

const CardValue = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: '16px',
  fontWeight: 500,
  color: '#1a1a1a'
});

const ArrowIcon = styled(ArrowForwardIcon)({
  fontSize: '20px',
  color: '#c9a227',
  opacity: 0,
  transform: 'translateX(-10px)',
  transition: 'all 0.3s ease'
});

const AboutSection = ({ businessInfo, contactInfo }) => {
  const description = businessInfo?.description ||
    'We are dedicated to providing exceptional services and creating lasting relationships with our clients. Our team of experts is committed to delivering outstanding results that exceed expectations.';

  const handlePhoneClick = () => {
    if (contactInfo?.phone) window.location.href = `tel:${contactInfo.phone}`;
  };

  const handleEmailClick = () => {
    if (contactInfo?.email) window.location.href = `mailto:${contactInfo.email}`;
  };

  const handleAddressClick = () => {
    if (contactInfo?.address) {
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(contactInfo.address)}`, '_blank');
    }
  };

  const contactItems = [
    { icon: LocationOnIcon, label: 'Location', value: contactInfo?.address, onClick: handleAddressClick },
    { icon: PhoneIcon, label: 'Phone', value: contactInfo?.phone, onClick: handlePhoneClick },
    { icon: EmailIcon, label: 'Email', value: contactInfo?.email, onClick: handleEmailClick },
    { icon: LanguageIcon, label: 'Website', value: contactInfo?.website, onClick: () => window.open(contactInfo?.website, '_blank') }
  ].filter(item => item.value);

  return (
    <SectionContainer id="about">
      <ContentWrapper>
        <MainContent>
          <LeftColumn>
            <SectionBadge>
              <BadgeText>About Us</BadgeText>
            </SectionBadge>

            <SectionTitle>
              We Create <GradientText>Experiences</GradientText>
            </SectionTitle>

            <DescriptionText>{description}</DescriptionText>

            <QuoteBlock>
              <QuoteText>
                "Excellence is not a destination but a continuous journey of improvement and dedication to our craft."
              </QuoteText>
            </QuoteBlock>
          </LeftColumn>

          <RightColumn>
            {contactItems.map((item, index) => (
              <InfoCard key={index} onClick={item.onClick}>
                <IconBox className="card-icon">
                  <item.icon />
                </IconBox>
                <CardContent>
                  <CardLabel>{item.label}</CardLabel>
                  <CardValue>{item.value}</CardValue>
                </CardContent>
                <ArrowIcon className="card-arrow" />
              </InfoCard>
            ))}
          </RightColumn>
        </MainContent>
      </ContentWrapper>
    </SectionContainer>
  );
};

export default AboutSection;
